import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Badge, Box, Button, Center, Heading, HStack, Icon, Input, InputGroup, Separator, SimpleGrid, Spinner, Text, VStack } from '@chakra-ui/react';
import { Switch } from '@src/components/chakra-ui/switch';
import { AdvisorDirectoryCard } from '@src/components/sco/Advisors/cards/AdvisorDirectoryCard';
import { directoryCardActions } from '@src/components/sco/Advisors/cards/directoryCardActions';
import { ADVISORS_DIRECTORY_PAGE_SIZE } from '@src/components/sco/Advisors/constants';
import { DIR_CATEGORIES } from '@src/components/sco/Advisors/data/dirCategories';
import { useAdvisorsCollectionLoadMore } from '@src/components/sco/Advisors/hooks/useAdvisorsCollectionLoadMore';
import { advisorsBreadcrumbsWithTrail, useAdvisorsToolbar } from '@src/components/sco/Advisors/hooks/useAdvisorsToolbar';
import { useBoardSeatCounts } from '@src/components/sco/Advisors/hooks/useBoardSeatCounts';
import { useDirectoryNamedSeatMatches } from '@src/components/sco/Advisors/hooks/useDirectoryNamedSeatMatches';
import { useIsMobile } from '@src/components/sco/Advisors/hooks/useIsMobile';
import { InviteToBoardModal } from '@src/components/sco/Advisors/modals/InviteToBoardModal';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorMarketplaceActionButton } from '@src/components/sco/Advisors/shared/AdvisorMarketplaceActionButton';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { advisorMarketplaceActions } from '@src/components/sco/Advisors/utils/advisorMarketplaceActions';
import { CATEGORY_KEY_TO_NAME } from '@src/components/sco/Advisors/utils/categoryKeys';
import { ACCEPTING_SESSIONS_QUERY_KEY, parseAcceptingSessionsFilter } from '@src/components/sco/Advisors/utils/directoryFilters';
import { NestedAppLayout, attachedNavPanelContainerProps, attachedPanelContainerProps } from '@src/components/sco/AppLayout';
import { SidebarNestedNavList, SidebarNestedNavListSection } from '@src/components/sco/SidebarNestedNavList';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_DIRECTORY } from '@src/gql/queries/AdvisorDirectory';
import { ScoAdvisorProfile } from '@src/graphql-types';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { LuLayoutGrid, LuSearch, LuSparkles, LuX } from 'react-icons/lu';

/**
 * Find advisors — split panel: filters (left) + results grid (wider right).
 * Category discovery also lives on `/advisors/browse`.
 */
export const DirectorySection = () => {
  const $ = _c(184);
  const paths = useAdvisorsPaths();
  const isMobile = useIsMobile();
  const router = useRouter();
  const initialQ = typeof router.query.q === "string" ? router.query.q : "";
  const [keywords, setKeywords] = useState(initialQ);
  const [searchKeywords, setSearchKeywords] = useState(initialQ || undefined);
  const [inviteUserId, setInviteUserId] = useState(null);
  const {
    byTargetUserId: namedSeatMatches,
    refetch: refetchSeatMatches
  } = useDirectoryNamedSeatMatches();
  const {
    counts: seatCounts
  } = useBoardSeatCounts();
  const hasOpenSeats = seatCounts.drafts + seatCounts.searching > 0;
  const {
    currentUser
  } = useCurrentUser();
  const isGuest = !currentUser || !!currentUser.isGuest;
  let t0;
  if ($[0] !== paths.directory) {
    t0 = advisorsBreadcrumbsWithTrail({
      title: "Find advisors",
      href: paths.directory
    });
    $[0] = paths.directory;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      breadcrumbs: t0
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  useAdvisorsToolbar(t1);
  const categoryKeyFromRoute = typeof router.query.categoryKey === "string" ? router.query.categoryKey : typeof router.query.category === "string" ? router.query.category : undefined;
  const t2 = router.query[ACCEPTING_SESSIONS_QUERY_KEY];
  let t3;
  if ($[4] !== t2) {
    t3 = parseAcceptingSessionsFilter(t2);
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const acceptingSessions = t3;
  const t4 = acceptingSessions || undefined;
  let t5;
  if ($[6] !== categoryKeyFromRoute || $[7] !== searchKeywords || $[8] !== t4) {
    t5 = {
      categoryKey: categoryKeyFromRoute,
      keywords: searchKeywords,
      acceptingSessions: t4,
      page: 1,
      pageSize: ADVISORS_DIRECTORY_PAGE_SIZE
    };
    $[6] = categoryKeyFromRoute;
    $[7] = searchKeywords;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = !router.isReady;
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = {
      variables: t5,
      fetchPolicy: "cache-and-network",
      skip: t6
    };
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const query = useQuery(ADVISOR_DIRECTORY, t7);
  let t8;
  if ($[13] !== query.data?.advisorDirectory?.items) {
    t8 = query.data?.advisorDirectory?.items ?? [];
    $[13] = query.data?.advisorDirectory?.items;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t8) {
    t9 = t8.filter(_temp);
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const firstPageAdvisors = t9;
  const t10 = query.fetchMore;
  const t11 = query.data?.advisorDirectory?.pagination?.hasNextPage;
  const t12 = acceptingSessions ? "1" : "0";
  let t13;
  if ($[17] !== categoryKeyFromRoute || $[18] !== searchKeywords || $[19] !== t12) {
    t13 = [categoryKeyFromRoute, searchKeywords, t12];
    $[17] = categoryKeyFromRoute;
    $[18] = searchKeywords;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const t14 = t13.join("|");
  let t15;
  if ($[21] !== firstPageAdvisors || $[22] !== query.fetchMore || $[23] !== t11 || $[24] !== t14) {
    t15 = {
      fetchMore: t10,
      field: "advisorDirectory",
      firstPageItems: firstPageAdvisors,
      hasNextPage: t11,
      resetKey: t14
    };
    $[21] = firstPageAdvisors;
    $[22] = query.fetchMore;
    $[23] = t11;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const {
    items: advisors,
    loadMore,
    loadingMore,
    hasNextPage
  } = useAdvisorsCollectionLoadMore(t15);
  let t16;
  let t17;
  if ($[26] !== router.isReady || $[27] !== router.query.q) {
    t16 = () => {
      if (!router.isReady) {
        return;
      }
      if (typeof router.query.q === "string") {
        setKeywords(router.query.q);
        setSearchKeywords(router.query.q);
      } else {
        setKeywords("");
        setSearchKeywords(undefined);
      }
    };
    t17 = [router.isReady, router.query.q];
    $[26] = router.isReady;
    $[27] = router.query.q;
    $[28] = t16;
    $[29] = t17;
  } else {
    t16 = $[28];
    t17 = $[29];
  }
  useEffect(t16, t17);
  const itemCount = query.data?.advisorDirectory?.pagination?.itemCount ?? advisors.length;
  const initialLoad = query.loading && !query.data;
  let t18;
  if ($[30] !== acceptingSessions || $[31] !== categoryKeyFromRoute || $[32] !== paths || $[33] !== router || $[34] !== searchKeywords) {
    t18 = next => {
      const category = next.category === null ? undefined : next.category ?? categoryKeyFromRoute ?? undefined;
      const q = next.q === null ? undefined : next.q ?? searchKeywords ?? undefined;
      const sessionsOn = next.acceptingSessions === null ? false : next.acceptingSessions === undefined ? acceptingSessions : next.acceptingSessions;
      router.replace(paths.withQuery(paths.directory, {
        category,
        q,
        [ACCEPTING_SESSIONS_QUERY_KEY]: sessionsOn ? "1" : undefined
      }), undefined, {
        shallow: true
      });
    };
    $[30] = acceptingSessions;
    $[31] = categoryKeyFromRoute;
    $[32] = paths;
    $[33] = router;
    $[34] = searchKeywords;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const replaceDirectoryQuery = t18;
  let t19;
  if ($[36] !== keywords || $[37] !== replaceDirectoryQuery) {
    t19 = () => {
      const trimmed = keywords.trim();
      setSearchKeywords(trimmed || undefined);
      replaceDirectoryQuery({
        q: trimmed || null
      });
    };
    $[36] = keywords;
    $[37] = replaceDirectoryQuery;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const handleSearch = t19;
  let t20;
  if ($[39] !== replaceDirectoryQuery) {
    t20 = () => {
      setKeywords("");
      setSearchKeywords(undefined);
      replaceDirectoryQuery({
        category: null,
        q: null,
        acceptingSessions: null
      });
    };
    $[39] = replaceDirectoryQuery;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const clearAllFilters = t20;
  const activeCategoryTitle = categoryKeyFromRoute ? CATEGORY_KEY_TO_NAME[categoryKeyFromRoute] || categoryKeyFromRoute : null;
  const hasActiveFilters = !!(categoryKeyFromRoute || searchKeywords || acceptingSessions);
  const q_0 = searchKeywords;
  const sessions = acceptingSessions ? "1" : undefined;
  let t21;
  if ($[41] !== paths || $[42] !== q_0 || $[43] !== sessions) {
    t21 = paths.withQuery(paths.directory, {
      q: q_0,
      [ACCEPTING_SESSIONS_QUERY_KEY]: sessions
    });
    $[41] = paths;
    $[42] = q_0;
    $[43] = sessions;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  const t22 = !categoryKeyFromRoute;
  let t23;
  if ($[45] !== t21 || $[46] !== t22) {
    t23 = {
      title: "All topics",
      href: t21,
      isActive: t22,
      activeExact: true
    };
    $[45] = t21;
    $[46] = t22;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== categoryKeyFromRoute || $[49] !== paths || $[50] !== q_0 || $[51] !== sessions || $[52] !== t23) {
    let t25;
    if ($[54] !== categoryKeyFromRoute || $[55] !== paths || $[56] !== q_0 || $[57] !== sessions) {
      t25 = cat => ({
        title: cat.name,
        href: paths.withQuery(paths.directory, {
          category: cat.key,
          q: q_0,
          [ACCEPTING_SESSIONS_QUERY_KEY]: sessions
        }),
        icon: cat.icon,
        isActive: categoryKeyFromRoute === cat.key,
        buttonProps: {
          _hover: {
            bg: "colorPalette.emphasized/20"
          }
        }
      });
      $[54] = categoryKeyFromRoute;
      $[55] = paths;
      $[56] = q_0;
      $[57] = sessions;
      $[58] = t25;
    } else {
      t25 = $[58];
    }
    t24 = [t23, ...DIR_CATEGORIES.map(t25)];
    $[48] = categoryKeyFromRoute;
    $[49] = paths;
    $[50] = q_0;
    $[51] = sessions;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  const topicSections = t24;
  const resultsLabel = acceptingSessions ? activeCategoryTitle ? `Accepting sessions in ${activeCategoryTitle}` : searchKeywords ? `Accepting sessions for “${searchKeywords}”` : "Advisors accepting booked sessions" : activeCategoryTitle ? `Advisors in ${activeCategoryTitle}` : searchKeywords ? `Results for “${searchKeywords}”` : "Approved advisors";
  let t25;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Text color="colorPalette.fg/60" textStyle="labelXs">Keywords</Text>;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Icon boxSize={4} color="colorPalette.solid"><LuSearch /></Icon>;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = event => setKeywords(event.currentTarget.value);
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== handleSearch) {
    t28 = event_0 => {
      if (event_0.key === "Enter") {
        handleSearch();
      }
    };
    $[62] = handleSearch;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== keywords || $[65] !== t28) {
    t29 = <InputGroup startElement={t26}><Input fontSize="sm" height="auto" paddingY={2.5} placeholder={"Name, headline, keyword\u2026"} value={keywords} onChange={t27} onKeyDown={t28} /></InputGroup>;
    $[64] = keywords;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== handleSearch) {
    t30 = <Button size="sm" variant="cta" width="full" onClick={handleSearch}>Search</Button>;
    $[67] = handleSearch;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] !== t29 || $[70] !== t30) {
    t31 = <VStack align="stretch" gap={2}>{t25}{t29}{t30}</VStack>;
    $[69] = t29;
    $[70] = t30;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  const searchField = t31;
  let t32;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <VStack align="stretch" gap={0.5} minWidth={0}><Text fontSize="sm" fontWeight="medium">Accepting booked sessions</Text><Text color="colorPalette.fg/60" fontSize="xs" lineHeight={1.4}>Only advisors who take paid or free video bookings.</Text></VStack>;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== replaceDirectoryQuery) {
    t33 = details => replaceDirectoryQuery({
      acceptingSessions: !!details.checked
    });
    $[73] = replaceDirectoryQuery;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] !== acceptingSessions || $[76] !== t33) {
    t34 = <HStack align="start" gap={3} justify="space-between">{t32}<Switch aria-label="Accepting booked sessions" checked={acceptingSessions} colorPalette="teal" onCheckedChange={t33} /></HStack>;
    $[75] = acceptingSessions;
    $[76] = t33;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  const sessionsSwitch = t34;
  let t35;
  if ($[78] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = {
      scrollbarWidth: "none",
      "&::-webkit-scrollbar": {
        display: "none"
      }
    };
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] !== topicSections) {
    t36 = topicSections.map(_temp2);
    $[79] = topicSections;
    $[80] = t36;
  } else {
    t36 = $[80];
  }
  let t37;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <Icon boxSize={3.5}><LuLayoutGrid /></Icon>;
    $[81] = t37;
  } else {
    t37 = $[81];
  }
  let t38;
  if ($[82] !== paths.browse) {
    t38 = <Button asChild={true} flexShrink={0} size="xs" variant="ghost"><NextLink href={paths.browse}>{t37}Browse</NextLink></Button>;
    $[82] = paths.browse;
    $[83] = t38;
  } else {
    t38 = $[83];
  }
  let t39;
  if ($[84] !== t36 || $[85] !== t38) {
    t39 = <HStack aria-label="Topics" as="nav" css={t35} gap={2} marginX={-4} overflowX="auto" paddingX={4} width="calc(100% + 2rem)">{t36}{t38}</HStack>;
    $[84] = t36;
    $[85] = t38;
    $[86] = t39;
  } else {
    t39 = $[86];
  }
  let t40;
  if ($[87] !== clearAllFilters || $[88] !== hasActiveFilters) {
    t40 = hasActiveFilters && <Button alignSelf="flex-start" size="xs" variant="outline" onClick={clearAllFilters}><Icon boxSize={3.5}><LuX /></Icon>Clear filters</Button>;
    $[87] = clearAllFilters;
    $[88] = hasActiveFilters;
    $[89] = t40;
  } else {
    t40 = $[89];
  }
  let t41;
  if ($[90] !== searchField || $[91] !== sessionsSwitch || $[92] !== t39 || $[93] !== t40) {
    t41 = <VStack align="stretch" gap={3} width="full">{searchField}{sessionsSwitch}{t39}{t40}</VStack>;
    $[90] = searchField;
    $[91] = sessionsSwitch;
    $[92] = t39;
    $[93] = t40;
    $[94] = t41;
  } else {
    t41 = $[94];
  }
  const mobileFilters = t41;
  let t42;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <Separator />;
    $[95] = t42;
  } else {
    t42 = $[95];
  }
  let t43;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <Separator />;
    $[96] = t43;
  } else {
    t43 = $[96];
  }
  let t44;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <Text color="colorPalette.fg/60" textStyle="labelXs">Topics</Text>;
    $[97] = t44;
  } else {
    t44 = $[97];
  }
  let t45;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <Icon boxSize={3.5}><LuLayoutGrid /></Icon>;
    $[98] = t45;
  } else {
    t45 = $[98];
  }
  let t46;
  if ($[99] !== paths.browse) {
    t46 = <HStack align="center" gap={2} justify="space-between" wrap="wrap">{t44}<Button asChild={true} size="xs" variant="ghost"><NextLink href={paths.browse}>{t45}Browse</NextLink></Button></HStack>;
    $[99] = paths.browse;
    $[100] = t46;
  } else {
    t46 = $[100];
  }
  let t47;
  if ($[101] !== topicSections) {
    t47 = <Box marginX={-3}><SidebarNestedNavList sections={topicSections} /></Box>;
    $[101] = topicSections;
    $[102] = t47;
  } else {
    t47 = $[102];
  }
  let t48;
  if ($[103] !== t46 || $[104] !== t47) {
    t48 = <VStack align="stretch" gap={2}>{t46}{t47}</VStack>;
    $[103] = t46;
    $[104] = t47;
    $[105] = t48;
  } else {
    t48 = $[105];
  }
  let t49;
  if ($[106] !== acceptingSessions || $[107] !== activeCategoryTitle || $[108] !== clearAllFilters || $[109] !== hasActiveFilters || $[110] !== searchKeywords) {
    t49 = hasActiveFilters && <><Separator /><VStack align="stretch" gap={2}>{activeCategoryTitle && <HStack color="colorPalette.fg/70" fontSize="xs" gap={1.5} wrap="wrap"><Text>Topic:{" "}<Text as="span" fontWeight="semibold">{activeCategoryTitle}</Text></Text></HStack>}{searchKeywords && <HStack color="colorPalette.fg/70" fontSize="xs" gap={1.5} wrap="wrap"><Text>Search:{" "}<Text as="span" fontWeight="semibold">“{searchKeywords}”</Text></Text></HStack>}{acceptingSessions && <HStack color="colorPalette.fg/70" fontSize="xs" gap={1.5} wrap="wrap"><Text>Filter:{" "}<Text as="span" fontWeight="semibold">Accepting booked sessions</Text></Text></HStack>}<Button size="xs" variant="outline" width="full" onClick={clearAllFilters}><Icon boxSize={3.5}><LuX /></Icon>Clear filters</Button></VStack></>;
    $[106] = acceptingSessions;
    $[107] = activeCategoryTitle;
    $[108] = clearAllFilters;
    $[109] = hasActiveFilters;
    $[110] = searchKeywords;
    $[111] = t49;
  } else {
    t49 = $[111];
  }
  let t50;
  if ($[112] !== isGuest) {
    t50 = !isGuest && <Box bg="colorPalette.solid/5" borderRadius="md" padding={3}><HStack align="start" color="colorPalette.fg/60" gap={2}><Icon boxSize={3.5} flexShrink={0} marginTop={0.5}><LuSparkles /></Icon><Text fontSize="xs" lineHeight={1.4}>Match % appears only when Member Match has scored this person for one of your open seats — never a fake rank.</Text></HStack></Box>;
    $[112] = isGuest;
    $[113] = t50;
  } else {
    t50 = $[113];
  }
  let t51;
  if ($[114] !== searchField || $[115] !== sessionsSwitch || $[116] !== t48 || $[117] !== t49 || $[118] !== t50) {
    t51 = <VStack align="stretch" gap={5} width="full">{searchField}{t42}{sessionsSwitch}{t43}{t48}{t49}{t50}</VStack>;
    $[114] = searchField;
    $[115] = sessionsSwitch;
    $[116] = t48;
    $[117] = t49;
    $[118] = t50;
    $[119] = t51;
  } else {
    t51 = $[119];
  }
  const filterPanel = t51;
  const t52 = isMobile ? undefined : "nowrap";
  const t53 = !initialLoad && itemCount > 0 ? ` · ${itemCount}` : "";
  let t54;
  if ($[120] !== resultsLabel || $[121] !== t52 || $[122] !== t53) {
    t54 = <Text color="colorPalette.fg/60" textStyle="labelXs" whiteSpace={t52}>{resultsLabel}{t53}</Text>;
    $[120] = resultsLabel;
    $[121] = t52;
    $[122] = t53;
    $[123] = t54;
  } else {
    t54 = $[123];
  }
  const resultsCount = t54;
  let t55;
  if ($[124] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = {
      base: 4,
      md: 6,
      lg: 8
    };
    $[124] = t55;
  } else {
    t55 = $[124];
  }
  const t56 = isMobile ? "full" : 0;
  const t57 = isMobile ? "xl" : "3xl";
  let t58;
  if ($[125] !== t57) {
    t58 = <Heading size={t57}>Find advisors</Heading>;
    $[125] = t57;
    $[126] = t58;
  } else {
    t58 = $[126];
  }
  const t59 = isMobile ? "sm" : "md";
  let t60;
  if ($[127] !== t59) {
    t60 = <Text color="colorPalette.fg/60" fontSize={t59}>Approved marketplace advisors for your board or paid sessions.</Text>;
    $[127] = t59;
    $[128] = t60;
  } else {
    t60 = $[128];
  }
  let t61;
  if ($[129] !== t56 || $[130] !== t58 || $[131] !== t60) {
    t61 = <VStack align="start" flex={1} gap={0.5} minWidth={t56}>{t58}{t60}</VStack>;
    $[129] = t56;
    $[130] = t58;
    $[131] = t60;
    $[132] = t61;
  } else {
    t61 = $[132];
  }
  const t62 = !isMobile && resultsCount;
  let t63;
  if ($[133] !== t61 || $[134] !== t62) {
    t63 = <HStack align="end" gap={3} justify="space-between" wrap="wrap">{t61}{t62}</HStack>;
    $[133] = t61;
    $[134] = t62;
    $[135] = t63;
  } else {
    t63 = $[135];
  }
  let t64;
  if ($[136] !== isMobile || $[137] !== mobileFilters || $[138] !== resultsCount) {
    t64 = isMobile && <>{mobileFilters}{resultsCount}</>;
    $[136] = isMobile;
    $[137] = mobileFilters;
    $[138] = resultsCount;
    $[139] = t64;
  } else {
    t64 = $[139];
  }
  let t65;
  if ($[140] !== initialLoad) {
    t65 = initialLoad && <Center flex={1} minHeight="12rem" padding={8}><Spinner /></Center>;
    $[140] = initialLoad;
    $[141] = t65;
  } else {
    t65 = $[141];
  }
  let t66;
  if ($[142] !== query.error) {
    t66 = query.error && <Text color="red.fg" fontSize="sm">{query.error.message}</Text>;
    $[142] = query.error;
    $[143] = t66;
  } else {
    t66 = $[143];
  }
  let t67;
  if ($[144] !== advisors || $[145] !== clearAllFilters || $[146] !== hasActiveFilters || $[147] !== initialLoad || $[148] !== paths.browse) {
    t67 = !initialLoad && advisors.length === 0 && <Center padding={8}><VStack gap={3} textAlign="center"><Heading size="md">No advisors found</Heading><Text color="colorPalette.fg/60" fontSize="sm">Try a different search or pick another topic.</Text><HStack gap={2} justify="center" wrap="wrap">{hasActiveFilters && <Button size="sm" variant="outline" onClick={clearAllFilters}>Clear filters</Button>}<Button asChild={true} size="sm" variant="ghost"><NextLink href={paths.browse}>Browse topics</NextLink></Button></HStack></VStack></Center>;
    $[144] = advisors;
    $[145] = clearAllFilters;
    $[146] = hasActiveFilters;
    $[147] = initialLoad;
    $[148] = paths.browse;
    $[149] = t67;
  } else {
    t67 = $[149];
  }
  let t68;
  if ($[150] !== advisors || $[151] !== currentUser?.userId || $[152] !== hasOpenSeats || $[153] !== initialLoad || $[154] !== isGuest || $[155] !== namedSeatMatches || $[156] !== paths) {
    t68 = !initialLoad && advisors.length > 0 && <SimpleGrid columns={{
      base: 1,
      sm: 2,
      xl: 3
    }} gap={4}>{advisors.map(advisor => {
        const user = advisor.user;
        const slug = user.slug || user.userId;
        const namedMatch = user.userId ? namedSeatMatches.get(user.userId) : undefined;
        const profileHref = `${paths.profile}/${slug}`;
        const {
          primary,
          secondary
        } = directoryCardActions(advisorMarketplaceActions({
          canAcceptSessions: advisor.canAcceptSessions,
          hasOpenSeats,
          isSelf: !!user.userId && user.userId === currentUser?.userId
        }).filter(action => !isGuest || action.key === "book" || action.key === "profile"));
        const renderAction = action_0 => <AdvisorMarketplaceActionButton key={action_0.key} action={action_0} bookingRef="directory" paths={paths} slug={slug} userId={user.userId} onInvite={() => setInviteUserId(user.userId)} />;
        return <AdvisorDirectoryCard key={advisor.advisorProfileId} actions={user.userId && primary ? renderAction(primary) : null} advisor={advisor} profileHref={profileHref} secondaryActions={user.userId ? secondary.map(renderAction) : undefined} badge={namedMatch ? <Badge colorPalette="green" title={namedMatch.badgeLabel} variant="subtle">{namedMatch.badgeLabel}</Badge> : null} />;
      })}</SimpleGrid>;
    $[150] = advisors;
    $[151] = currentUser?.userId;
    $[152] = hasOpenSeats;
    $[153] = initialLoad;
    $[154] = isGuest;
    $[155] = namedSeatMatches;
    $[156] = paths;
    $[157] = t68;
  } else {
    t68 = $[157];
  }
  let t69;
  if ($[158] !== hasNextPage || $[159] !== loadMore || $[160] !== loadingMore) {
    t69 = <AdvisorsLoadMore hasNextPage={hasNextPage} loading={loadingMore} onLoadMore={loadMore} />;
    $[158] = hasNextPage;
    $[159] = loadMore;
    $[160] = loadingMore;
    $[161] = t69;
  } else {
    t69 = $[161];
  }
  let t70;
  if ($[162] !== t63 || $[163] !== t64 || $[164] !== t65 || $[165] !== t66 || $[166] !== t67 || $[167] !== t68 || $[168] !== t69) {
    t70 = <VStack align="stretch" gap={4} marginX="auto" maxWidth="6xl" padding={t55} width="full">{t63}{t64}{t65}{t66}{t67}{t68}{t69}</VStack>;
    $[162] = t63;
    $[163] = t64;
    $[164] = t65;
    $[165] = t66;
    $[166] = t67;
    $[167] = t68;
    $[168] = t69;
    $[169] = t70;
  } else {
    t70 = $[169];
  }
  const resultsPanel = t70;
  let t71;
  if ($[170] !== filterPanel || $[171] !== isMobile) {
    t71 = isMobile ? [] : [{
      id: "advisors-directory-nav",
      defaultWidth: 280,
      minWidth: 220,
      maxWidth: 360,
      isResizable: true,
      resizeHandlePosition: "right",
      useDrawerBreakpoints: {
        base: true,
        md: false
      },
      containerProps: attachedNavPanelContainerProps,
      children: <Box height="full" minHeight={0} overflowY="auto" padding={{
        base: 4,
        md: 5
      }} width="full">{filterPanel}</Box>
    }];
    $[170] = filterPanel;
    $[171] = isMobile;
    $[172] = t71;
  } else {
    t71 = $[172];
  }
  let t72;
  if ($[173] !== resultsPanel) {
    t72 = {
      id: "advisors-directory-main",
      containerProps: attachedPanelContainerProps,
      children: <Box flex={1} height="full" minHeight={0} minWidth={0} overflowY="auto" width="full">{resultsPanel}</Box>
    };
    $[173] = resultsPanel;
    $[174] = t72;
  } else {
    t72 = $[174];
  }
  let t73;
  if ($[175] !== t71 || $[176] !== t72) {
    t73 = <NestedAppLayout id="advisors-directory" panels={[...t71, t72]} />;
    $[175] = t71;
    $[176] = t72;
    $[177] = t73;
  } else {
    t73 = $[177];
  }
  let t74;
  if ($[178] !== inviteUserId || $[179] !== refetchSeatMatches) {
    t74 = inviteUserId && <InviteToBoardModal advisorUserId={inviteUserId} isOpen={!!inviteUserId} onClose={() => setInviteUserId(null)} onSuccess={() => {
      refetchSeatMatches();
    }} />;
    $[178] = inviteUserId;
    $[179] = refetchSeatMatches;
    $[180] = t74;
  } else {
    t74 = $[180];
  }
  let t75;
  if ($[181] !== t73 || $[182] !== t74) {
    t75 = <>{t73}{t74}</>;
    $[181] = t73;
    $[182] = t74;
    $[183] = t75;
  } else {
    t75 = $[183];
  }
  return t75;
};
function _temp(item) {
  return !!item?.user?.userId;
}
function _temp2(topic) {
  return <Button key={String(topic.href)} asChild={true} flexShrink={0} size="xs" variant={topic.isActive ? "subtle" : "outline"}><NextLink aria-current={topic.isActive ? "page" : undefined} href={topic.href}>{topic.icon ? <Icon as={topic.icon} boxSize={3.5} /> : null}{topic.title}</NextLink></Button>;
}